import React from 'react';
import { observable, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import PropTypes from 'prop-types';
import { Trans, withNamespaces } from 'react-i18next';
import { differenceInDays } from 'date-fns';
import { analytics } from 'core/analytics';
import { staticURL } from 'core/url';
import { FullWidthDivider, SettingsSection } from 'legacyComponents/styled/lib';

import { CanceledPlanDescription } from 'apps/payment/subscription/CanceledPlanDescription';
import { SectionTitle } from 'apps/group/styles';
import { Spinner, Tooltip } from 'components';
import { SubscriptionAppManager } from 'apps/payment/subscription/appManager';
import { PlanDescription } from 'apps/payment/subscription/planDescription';
import { StorageUsage } from 'apps/payment/subscription/storageUsage';
import { SeatsBreakdown } from 'apps/payment/subscription/seatsBreakdown';
import { clientStore } from 'apps/client/app';
import { SubscriptionType } from 'constants/subscription';
import { isTrialExpired } from 'utils/freeTrials';
import { capitalizeFirstLetter } from 'utils/formatStrings';

import { AddSeatsModal } from './AddSeatsModal';
import { ConversionUsage } from './ConversionUsage';

const LegacyPlan = ({ isLegacySubscription, isLegacy, withoutBrackets }) => {
  return !isLegacySubscription && isLegacy ? (
    <>
      <Tooltip
        maxWidth='400px'
        delayShow={200}
        place='bottom'
        id='legacy-plan'
      />
      <span
        className='ml-2'
        data-for='legacy-plan'
        data-tip="You can continue to use this legacy plan. If you cancel it, any outside collaborators would be changed to the viewer role and you won't be able to re-subscribe on the same deal."
      >
        {withoutBrackets ? ', Legacy Plan' : '(Legacy Plan)'}
      </span>
    </>
  ) : null;
};

@withNamespaces()
@inject('routerStore')
@observer
export class BillingSubscription extends React.Component {
  @observable showAddSeatsModal = false;
  constructor(props) {
    super(props);
    const {
      routerStore: { params },
    } = props;
    const spaceSlug = params.get('spacePath');
    this.appManager = new SubscriptionAppManager(null, { spaceSlug });
  }

  componentDidMount() {
    const {
      routerStore: { params },
    } = this.props;
    if (params.get('subscribe')) {
      params.set('subscribe', false);
      this.subscribe();
    }
  }

  subscribe = () => {
    const { paymentSpace, paymentSpacePreview } = this.appManager;
    if (!paymentSpace) return;
    analytics.sendEvent('AddToCart', {}, { twitter: true, facebook: true });
    if (
      !paymentSpacePreview ||
      (paymentSpacePreview && !paymentSpacePreview.preview)
    ) {
      this.appManager.NewSubscriptionQuery({ preview: true });
    }
    paymentSpace.ConfirmSubscribe();
  };

  @action closeModal = () => {
    this.showAddSeatsModal = false;
  };

  @action openSeatsModal = () => {
    this.showAddSeatsModal = true;
  };

  render() {
    const {
      loading,
      paymentSpace,
      loadingPreview,
      paymentSpacePreview,
      addSeatsPreviewResult,
      seatsUsage,
      storageUsage,
      conversionUsage,
    } = this.appManager;

    const { t } = this.props;

    if (loading || !paymentSpace) {
      return (
        <SettingsSection>
          <SectionTitle level={2} size='medium'>
            {t('Loading Plan')}
          </SectionTitle>
          <FullWidthDivider />
          <Spinner margin='20px auto' />
        </SettingsSection>
      );
    }

    const { subscription, inSpace, isEarlyAccess } = paymentSpace;
    const trialPeriodEnd = inSpace && inSpace.trialPeriodEnd;
    const { activeSpace } = clientStore;
    const { flags } = inSpace || {};
    const { subscriptionType: planType, spaceType } = activeSpace || {};

    const subscriptionType = planType === 'TEAM' ? 'PRO' : planType;

    const isFreeSubscription =
      subscriptionType === SubscriptionType.FREE || !subscription;

    const isInitiative = spaceType === 'INITIATIVE';
    const hasProSubscription = !!subscription;

    let showSeatsBreakdown = true;

    if (!isInitiative && isFreeSubscription && !isEarlyAccess) {
      showSeatsBreakdown = false;
    }

    const isTrialActive = trialPeriodEnd && !isTrialExpired(trialPeriodEnd);

    const isLegacySubscription =
      subscription?.plan?.isLegacy &&
      subscription.isBillingCancelledInStripe &&
      !isTrialActive;

    if (isLegacySubscription) {
      showSeatsBreakdown = false;
    }

    let currentPlanType = t(
      `${capitalizeFirstLetter(subscriptionType) || ''} Plan`,
    );

    if (!isEarlyAccess && (isFreeSubscription || !hasProSubscription)) {
      currentPlanType = t('Starter Plan');
    }

    const getPlanDescription = () => {
      if (isFreeSubscription) {
        return {
          explain: '',
        };
      }

      const { currentPeriodEnd, isCancelled } = subscription;

      if (isCancelled && !isLegacySubscription) {
        return {
          explain: isTrialActive ? t('(Free Trial)') : '',
          description: (
            <CanceledPlanDescription
              canceledData={currentPeriodEnd}
              status={subscription.status}
            />
          ),
        };
      }

      const setToMidnight = date => {
        const newDate = new Date(date);
        newDate.setHours(0, 0, 0, 0);
        return newDate;
      };

      const remainingNumber = `${differenceInDays(
        setToMidnight(new Date(currentPeriodEnd)),
        setToMidnight(new Date()),
      )}`;

      const hasRemainingDay = remainingNumber >= 1;

      if (isTrialActive) {
        return {
          explain: <Trans>(Free Trial)</Trans>,
          description: hasRemainingDay && (
            <p className='flex flex-end text-black'>
              <div
                className='mr-1'
                data-testid='billingSettingsPage-daysRemaining-text'
              >
                {remainingNumber > 1
                  ? t(`${remainingNumber} days remaining`)
                  : t(`${remainingNumber} day remaining`)}
              </div>
              <img
                src={staticURL(`assets/icons/remaining.svg`)}
                alt='plan description'
              />
            </p>
          ),
        };
      }

      return {};
    };

    const { explain, description } = getPlanDescription();

    return (
      <SettingsSection style={{ marginBottom: '2rem' }}>
        <div
          className={`flex justify-between items-center text-black ${
            description ? '' : 'mb-4'
          }`}
        >
          <div className='flex items-center'>
            <SectionTitle style={{ paddingBottom: 0 }} level={2} size='medium'>
              <span data-testid='billingSettingsPage-planType-text'>
                {currentPlanType}
              </span>
              {explain && (
                <span
                  style={{ fontWeight: 400 }}
                  data-testid='billingSettingsPage-planExplain-text'
                >
                  &nbsp;{explain}
                </span>
              )}
            </SectionTitle>
            {!explain && (
              <LegacyPlan
                isLegacy={subscription?.plan?.isLegacy}
                isLegacySubscription={isLegacySubscription}
              />
            )}
          </div>
          {description}
        </div>
        <FullWidthDivider />

        <PlanDescription
          paymentSpace={paymentSpace}
          subscription={subscription}
          isInitiative={isInitiative}
          isLegacySubscription={isLegacySubscription}
          trialPeriodEnd={trialPeriodEnd}
        />

        {showSeatsBreakdown && (
          <div className='mt-8'>
            <SeatsBreakdown
              loading={loading}
              seatsUsage={seatsUsage}
              isInitiative={isInitiative}
              paymentSpace={paymentSpace}
              onClickAddSeats={this.openSeatsModal}
              onRemoveUnassignedSeats={this.removeUnassignedSeats}
            />
          </div>
        )}

        <StorageUsage
          loading={loading}
          storageUsage={storageUsage}
          subscriptionType={
            isEarlyAccess ? SubscriptionType.FREE : subscriptionType
          }
        />

        {(!subscription ||
          isEarlyAccess ||
          subscription.isBillingCancelledInStripe) &&
          !isInitiative &&
          clientStore.isInProfileSpace && (
            <ConversionUsage conversionUsage={conversionUsage} />
          )}

        <AddSeatsModal
          paymentSpace={paymentSpace}
          loadingPreview={loadingPreview}
          paymentSpacePreview={paymentSpacePreview}
          open={this.showAddSeatsModal}
          onClose={this.closeModal}
          addSeatsPreviewResult={addSeatsPreviewResult}
        />
      </SettingsSection>
    );
  }
}

BillingSubscription.propTypes = {
  routerStore: PropTypes.object,
};
